<template>
    <div>
        <base-wrapper
            :cardIcon="cardIcon"
            :cardText="cardText"
            :cardValue="cardValue"
            :layoutConfig="layoutConfig"
            :showText="showStatusText"
            :styles="getCardStyles"
            :widget="data"
            @show-modal="onShowModal"
            class="queue-card"
            v-bind="$attrs"
            v-on="$listeners"
            usedDynamicStatuses
            :status="queueType"
            :selectedType="selectedType"
            queue
            use-sound-animation
        />
        <update-dialog
            :layoutConfig="layoutConfig"
            :model="model"
            :visible.sync="showModal"
            @on-change="onChange"
            v-if="showModal">
            <template v-slot:content>
                <el-form @submit.native.prevent="onChange">
                    <el-form-item :label="$t('queue.counter.type')" class="font-bold">
                        <el-select
                            filterable
                            v-model="selectedType">
                            <el-option
                                :key="index"
                                :label="$t(type)"
                                :value="index"
                                class="queue-type"
                                v-for="(type, index) in availableTypes">
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item v-if="useContentTitle">
                        <div class="-mt-4 mb-2">
                            <label class="main-text-color">{{ $t('widget.contentTitle') }}</label>
                            <el-input v-model="ContentTitle"/>
                        </div>
                    </el-form-item>
                    <div class="py-4 flex">
                        <el-checkbox v-model="showStatusText">
                            {{ $t('status.show.text') }}
                        </el-checkbox>
                        <el-checkbox class="px-4" v-model="displayItemBorder">
                            {{ $t('status.display.border') }}
                        </el-checkbox>
                    </div>
                </el-form>
            </template>
            <template v-slot:additional-data>
                <el-collapse v-model="activeCollapse" class="pt-2">
                    <el-collapse-item :title="$t('settings.sounds')" name="sounds">
                        <div class="flex justify-between mb-1">
                            <div>
                                <label class="main-text-color">{{ $t('widget.config.maximumWarningRangeValue') }}</label>:
                            </div>
                            <div v-if="selectedType" class="queue-time-picker">
                                <base-time-input
                                    v-model="WarningMaxTime"
                                    :min-value="0"
                                    :step="1"
                                    class="input-number"
                                    max-width="150px"
                                    color="primary"
                                />
                            </div>
                            <el-input-number
                                v-else
                                :max="100" :min="0" :step="1" type="number"
                                v-model="WarningMaxCounter"
                                size="small"
                            />
                        </div>
                        <div class="flex justify-between">
                            <div>
                                <label class="main-text-color">{{ $t('widget.config.maximumLimitRangeValue') }}</label>:
                            </div>
                            <div v-if="selectedType" class="queue-time-picker">
                                <base-time-input
                                    v-model="LimitMaxTime"
                                    :min-value="0"
                                    :step="1"
                                    class="input-number"
                                    max-width="150px"
                                    color="primary"
                                />
                            </div>
                            <el-input-number
                                v-else
                                :max="100" :min="0" :step="1" type="number"
                                v-model="LimitMaxCounter"
                                size="small"
                            />
                        </div>
                        <div class="flex flex-col">
                            <div class="flex justify-between mt-1">
                                <div class="color-block">
                                    <span class="text-main-sm whitespace-nowrap main-text-color">
                                        {{ $t('settings.warningColor') }}:
                                    </span>
                                </div>
                                <div class="flex items-center justify-end">
                                    <slot>
                                        <IconColorPicker
                                            class="w-5 h-5 icon-color-picker mx-2"
                                            :style="getIndicatorWarningColor"
                                        />
                                    </slot>
                                    <el-color-picker
                                        :class="$rtl.isRTL ? 'icon-color-picker-rtl' : 'icon-color-picker-ltr'"
                                        :predefine="predefinedColors"
                                        v-model="warningColor"
                                    />
                                </div>
                            </div>
                            <div class="flex justify-between mt-1">
                                <div class="color-block">
                                    <span class="text-main-sm whitespace-nowrap main-text-color">
                                        {{ $t('settings.limitColor') }}:
                                    </span>
                                </div>
                                <div class="flex items-center justify-end">
                                    <slot>
                                        <IconColorPicker
                                            class="w-5 h-5 icon-color-picker mx-2"
                                            :style="getIndicatorLimitColor"
                                        />
                                    </slot>
                                    <el-color-picker
                                        :class="$rtl.isRTL ? 'icon-color-picker-rtl' : 'icon-color-picker-ltr'"
                                        :predefine="predefinedColors"
                                        v-model="limitColor"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="py-2 flex justify-between items-center">
                            <div class="flex items-center">
                                <IconColorPicker
                                    class="w-5 h-5 icon-color-picker"
                                    :style="getIndicatorWarningColor"
                                />
                                <div class="flex items-center mx-2 text-main-sm whitespace-nowrap main-text-color">
                                    {{ $t('settings.warningSound') }}:
                                </div>
                            </div>
                            <SoundSelect
                                v-model="soundWarning"
                                :placeholder="$t('general.account')"
                                :options="warningSounds"
                            />
                        </div>
                        <div class="py-2 flex justify-between items-center">
                            <div class="flex items-center">
                                <IconColorPicker
                                    class="w-5 h-5 icon-color-picker"
                                    :style="getIndicatorLimitColor"
                                />
                                <div class="flex items-center mx-2 text-main-sm whitespace-nowrap main-text-color">
                                    {{ $t('settings.limitSound') }}:
                                </div>
                            </div>
                            <SoundSelect
                                v-model="soundLimit"
                                :placeholder="$t('general.account')"
                                :options="errorSounds"
                            />
                        </div>
                    </el-collapse-item>
                </el-collapse>
                <el-collapse class="pt-4" v-if="autoCompletes.length" v-model="activeCollapse">
                    <el-collapse-item :title="$t('settings.filters')" name="filters">
                        <auto-complete
                            :key="index"
                            :model="model.WidgetConfig[index]"
                            v-for="(filter, index) in model.WidgetConfig"
                            v-if="isAutoComplete(filter)"/>
                    </el-collapse-item>
                </el-collapse>
            </template>
            <template v-slot:footer>
                <div class="main-bg-light border-t-2 border-gray-300 py-4 px-10 flex items-center justify-between">
                    <cancel-button
                        @on-click="showModal = false"
                    />
                    <confirm-button
                        :label="$t('common.save')"
                        icon="IconSave"
                        @on-click="onChange"
                    />
                </div>
            </template>
        </update-dialog>
    </div>
</template>
<script>
    import { set } from 'vue'
    import cloneDeep from 'lodash/cloneDeep'
    import { Checkbox, Option, Select, Tooltip, Collapse, CollapseItem, InputNumber, ColorPicker, TimePicker } from 'element-ui'
    import { typeKeys, typeNames, types } from '@/enum/queueCounters'
    import { EditIcon, MoreVerticalIcon, TrashIcon } from 'vue-feather-icons'
    import { defaultCardColors } from '@/enum/defaultWidgetSettings'
    import queueMixin from '@/mixins/queueMixin'
    import { getInitialTime } from '@/util/timeUtils'
    import Timer from '@/util/Timer';
    import cardWidgetMixin from '@/mixins/cardWidgetMixin'
    import { getOptionsList } from '@/helpers/entitiesList';
    import get from 'lodash/get';
    import { predefinedColors } from '@/views/DashboardSettings/LayoutManagement/layout-management'
    import SoundSelector from '@/views/DashboardSettings/LayoutManagement/components/SoundSelector.vue'
    import BaseTimeInput from '@/components/Common/BaseTimeInput'
    import SoundSelect from "@/components/Common/SoundSelect.vue";

    export default {
        mixins: [queueMixin, cardWidgetMixin],
        props: {
            queues: {
                type: Array,
                default: () => [],
            },
            queueType: {
                type: Number,
                default: () => 0,
            },
            showText: {
                type: Boolean,
                default: () => true,
            },
            displayBorder: {
                type: Boolean,
                default: true,
            },
            data: {
                type: Object,
                default: () => ({}),
            },
        },
        components: {
            SoundSelect,
            TrashIcon,
            EditIcon,
            MoreVerticalIcon,
            UpdateDialog: () => import('./UpdateDialog'),
            AutoComplete: () => import('@/components/Widgets/WidgetUpdateForm/Filters/AutoComplete'),
            [Select.name]: Select,
            [Option.name]: Option,
            [Tooltip.name]: Tooltip,
            [Checkbox.name]: Checkbox,
            [Collapse.name]: Collapse,
            [CollapseItem.name]: CollapseItem,
            CancelButton: () => import("@/components/Common/Buttons/CancelButton"),
            ConfirmButton: () => import("@/components/Common/Buttons/ConfirmButton"),
            [InputNumber.name]: InputNumber,
            [ColorPicker.name]: ColorPicker,
            SoundSelector,
            [TimePicker.name]: TimePicker,
            BaseTimeInput
        },
        data() {
            return {
                showModal: false,
                selectedType: this.queueType,
                availableTypes: typeNames,
                showStatusText: this.showText,
                displayItemBorder: this.displayBorder,
                timeout: null,
                timer: new Timer(),
                model: {},
                layoutConfig: {},
                AUTO_COMPLETE_PARAMETER_TYPE: 6,
                activeCollapse: ['filters', 'sounds'],
                predefinedColors,
                limitColor: '',
                warningColor: '',
                soundWarning: 'none',
                soundLimit: 'none',
                WarningMaxCounter: 0,
                LimitMaxCounter: 0,
                WarningMaxTime: 0,
                LimitMaxTime: 0,
                typeKeys,
                ContentTitle: '',
                cardDefaultText: this.$t(types[this.queueType].text)
            }
        },
        computed: {
            useContentTitle () {
                return this.queueType === this.typeKeys.CALLERS_ID
            },
            autoCompletes() {
                return this.data.WidgetConfig.filter(c => c.ParameterType === this.AUTO_COMPLETE_PARAMETER_TYPE)
            },
            allEntityQueues() {
                return getOptionsList('{|queue_list|}')
            },
            queueToDisplay() {
                return get(this.data.WidgetConfig, '[0].WidgetParameterValueJson.EntityPositive', [])
            },
            filteredQueue() {
                if (this.queueToDisplay.length) {
                    return this.allQueues.filter(e => this.queueToDisplay.includes(e.QueueID))
                } else {
                    return this.allQueues
                }
            },
            cardValue() {
                if (this.selectedType === typeKeys.CALLERS_ID) {
                    return this.getQueueStats().queueCalls
                }
                return this.timer.displayTime
            },
            cardIcon() {
                return types[this.queueType].icon
            },
            cardText() {
                return this.data.WidgetLayout.ContentTitle || this.cardDefaultText
            },
            getIndicatorLimitColor() {
                return {
                    'color': this.limitColor
                }
            },
            getIndicatorWarningColor() {
                return {
                    'color': this.warningColor
                }
            },
            warningSounds () {
                return this.$store.getters['entities/getWarningSounds']
            },
            errorSounds () {
                return this.$store.getters['entities/getErrorSounds']
            },
        },
        methods: {
            getQueueStats() {
                let minJoinTimeStamp = new Date().getTime() * 10000
                let queueCalls = 0
                this.filteredQueue.forEach((queue) => {
                    queue.Calls.forEach((call) => {
                        if (call.JoinTimeStamp < minJoinTimeStamp) {
                            minJoinTimeStamp = call.JoinTimeStamp
                        }
                        queueCalls++
                    })
                })
                return { queueCalls, minJoinTimeStamp }
            },
            getInitialQueueTime() {
                if (this.selectedType !== typeKeys.MAXIMUM_WAITING_ID) {
                    return 0
                }

                const queueStats = this.getQueueStats()
                if (queueStats.queueCalls === 0) {
                    return 0
                }
                return getInitialTime(queueStats.minJoinTimeStamp)
            },
            onChange() {
                try {
                    this.model.WidgetConfig.forEach((config) => {
                        if (typeof config.WidgetParameterValueJson === 'object') {
                            config.WidgetParameterValueJson['AccountList'] = [this.$store.state.entities.selectedAccountID]
                        }
                        if (typeof config.WidgetParameterValue === 'object') {
                            config.WidgetParameterValue['AccountList'] = [this.$store.state.entities.selectedAccountID]
                            config.WidgetParameterValue = JSON.stringify(config.WidgetParameterValue)
                        }
                    })
                } catch (e) {
                    console.warn(e)
                }

                let data = {
                    queueType: this.selectedType,
                    showText: this.showStatusText,
                    displayBorder: this.displayItemBorder,
                    limitColor: this.limitColor,
                    warningColor: this.warningColor,
                    soundWarning: this.soundWarning,
                    soundLimit: this.soundLimit,
                    WarningMaxCounter: this.WarningMaxCounter,
                    LimitMaxCounter: this.LimitMaxCounter,
                    WarningMaxTime: this.WarningMaxTime,
                    LimitMaxTime: this.LimitMaxTime
                }

                this.data.WidgetLayout = {
                    ...this.data.WidgetLayout,
                    ...data,
                    ...this.layoutConfig,
                    colors: this.model.colors,
                }
                if (this.useContentTitle) {
                    set(this.data.WidgetLayout, 'ContentTitle', this.ContentTitle || this.cardDefaultText)
                }

                this.data.WidgetConfig = this.model.WidgetConfig

                this.$emit('on-update', this.data);
                this.showModal = false;
            },
            onShowModal() {
                this.showModal = true
            },
            setCounterState() {
                let callCount = this.getQueueStats().queueCalls
                this.timer.stop()
                this.timer.setValue(this.getInitialQueueTime())
                if (callCount !== 0) {
                    this.timer.start()
                }
            },
            isAutoComplete(WidgetConfig) {
                return WidgetConfig.ParameterType === this.AUTO_COMPLETE_PARAMETER_TYPE
            }
        },
        beforeDestroy() {
            this.timer.destroy()
        },
        watch: {
            data: {
                deep: true,
                immediate: true,
                handler(widget) {
                    this.model = cloneDeep(widget)
                    this.model.colors = cloneDeep(widget.WidgetLayout.colors || defaultCardColors)
                    if (this.useContentTitle) {
                        this.ContentTitle = this.model.WidgetLayout.ContentTitle || this.cardDefaultText
                    }
                    this.limitColor = this.model.WidgetLayout.limitColor || ''
                    this.warningColor = this.model.WidgetLayout.warningColor || ''
                    this.soundWarning = this.model.WidgetLayout.soundWarning || 'none'
                    this.soundLimit = this.model.WidgetLayout.soundLimit || 'none'
                    this.WarningMaxCounter = this.model.WidgetLayout.WarningMaxCounter || 0
                    this.LimitMaxCounter = this.model.WidgetLayout.LimitMaxCounter || 0
                    this.WarningMaxTime = this.model.WidgetLayout.WarningMaxTime || 0
                    this.LimitMaxTime = this.model.WidgetLayout.LimitMaxTime || 0
                    this.queueType = this.model.WidgetLayout.queueType || 0
                },
            },
            selectedType: {
                immediate: true,
                handler() {
                    this.setCounterState()
                },
            },
            filteredQueue() {
                this.setCounterState()
            },
        },
    }
</script>
<style lang="scss" scoped>
.el-form-item {
    @apply w-full;
    .el-select {
        @apply w-full;
    }
}

.el-select-dropdown.is-multiple .el-select-dropdown__item.selected {
    color: var(--primary-color);
}

.queue-card /deep/ .card-value {
    min-width: 80px;
    @apply flex justify-center;
}
.color-block {
    width: 130px;
}
::v-deep .el-date-editor.el-input {
    width: 150px !important;
}
</style>
